// TRANSLATIONS TO REVIEW

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import BottomButton from "../BottomButton";
import ColoredIcon from "../ColoredIcon";
import Color from "../../modules/Color";
import DefaultIcon from "../../img/default_icon.svg";
import GoogleIcon from "../../img/Google.svg";
import Account from "../../api/Account";
import { useSnackbar } from "../../contexts/SnackbarContext";
import Brand from "../../api/Brand";
import { TokenResponse, useGoogleLogin } from "@react-oauth/google";
import TextField from "../TextField";
import PasswordField from "../PasswordField";
import { useCookies } from "react-cookie";
import { FullScreenLoader } from "../FullScreenLoader";
import OnboardingShell from "../OnboardingShell";
import { getAppLanguage } from "../../utils/languageUtils";
import { getEnvironment } from "../../utils/getEnvironment";
import { trackPixelEvent } from "../../utils/metaPixel";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../constants/styles";
const environment = getEnvironment();

const SignUp = () => {
    const { showSnackbar } = useSnackbar();
    const [token, setToken] = useState<TokenResponse | undefined>();
    const [brand, setBrand] = useState<
        | {
              name: string;
              address: string;
              color: Color;
              logo?: string;
              business: string;
          }
        | undefined
    >();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [validatedLogo, setValidatedLogo] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);

    const [cookies, setCookie, removeCookie] = useCookies([
        "creation_uuid",
        "refBrandAddress",
        "uuid",
    ]);

    const emailInputRef = useRef<HTMLInputElement>(null);
    const passwordInputRef = useRef<HTMLInputElement>(null);

    // For claiming existing demo brand
    const searchParams = useMemo(
        () => new URLSearchParams(window.location.search),
        []
    );
    const claimBrandId = searchParams.get("claim_brand");
    const claimBrandName = searchParams.get("name");
    const isClaimingPreviewBrand = !!claimBrandId;

    useEffect(() => {
        if (claimBrandId !== null) {
            setBrand({
                name: claimBrandName || "your app",
                address: "placeholder",
                color: new Color(0, 0, 0),
                business: "1",
            });
            return;
        } else {
            const brand_loaded = sessionStorage.getItem("brand");
            if (brand_loaded) {
                const brand_raw: {
                    name: string;
                    address: string;
                    color: string;
                    logo?: string;
                    business: string;
                } = JSON.parse(brand_loaded);
                setBrand({
                    name: brand_raw.name,
                    address: brand_raw.address,
                    color: Color.fromJSON(brand_raw.color),
                    logo: brand_raw.logo,
                    business: brand_raw.business,
                });
                if (brand_raw.logo) {
                    const img = new Image();
                    img.onload = () => setValidatedLogo(brand_raw.logo);
                    img.onerror = () => setValidatedLogo(undefined);
                    img.src = brand_raw.logo;
                }
            } else {
                window.location.href = "/creation/brand";
            }
        }
    }, [claimBrandId, claimBrandName]);

    const onGoogleLoginError = () => console.error("Google login error.");
    const googleLogin = useGoogleLogin({
        onSuccess: (res) => setToken(res),
        onError: () => onGoogleLoginError(),
    });

    const handleBrandRegistration = (uuid: string) => {
        if (!brand) return;

        // Both signup paths converge here once the account UUID exists and
        // the backend has created the 14-day trial. Fire CompleteRegistration
        // so Meta can attribute it to the originating Lead click from the
        // landings and optimize ad delivery for actual trial signups.
        trackPixelEvent("CompleteRegistration", {
            content_name: "trial-signup",
            status: "complete",
        });

        if (!isClaimingPreviewBrand) {
            setCookie("creation_uuid", uuid);
            Brand.register(
                uuid,
                brand.name,
                brand.address,
                brand.color,
                "",
                brand.business,
                brand.logo,
                cookies.refBrandAddress || ""
            )
                .then(() => {
                    removeCookie("refBrandAddress");
                    setTimeout(() => {
                        window.location.href = "/creation/completed";
                    }, 100);
                })
                .catch((err) => {
                    console.error("Brand registration failed:", err);
                    showSnackbar(
                        "Something went wrong while creating your app. Please try again."
                    );
                });
        } else {
            const currentLanguage = getAppLanguage();
            const redirectUrl = `${environment.admin_address}/redirect/${uuid}?lang=${currentLanguage}`;
            setTimeout(() => {
                window.location.href = redirectUrl;
            }, 100);
        }
    };

    useEffect(() => {
        if (!token || !brand) return;
        Account.googleSignUp(token.access_token, claimBrandId ?? undefined)
            .then((uuid) => {
                if (!uuid) {
                    showSnackbar("Email is already used.");
                    return;
                }
                handleBrandRegistration(uuid);
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, brand]);

    const standardSignUp = async () => {
        if (!brand) return;
        if (name.trim() === "" || email.trim() === "" || password === "") {
            showSnackbar("Fill all the fields.", "warning");
            return;
        }
        setLoading(true);
        await Account.signUp(
            name,
            email,
            password,
            isClaimingPreviewBrand ? claimBrandId : undefined
        )
            .then((uuid) => {
                if (!uuid) {
                    showSnackbar("Email is already used.");
                    return;
                }
                handleBrandRegistration(uuid);
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
            });
    };

    if (!brand) return <FullScreenLoader />;

    // Brand icon for the hero area
    const heroContent =
        !isClaimingPreviewBrand && brand ? (
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "18px",
                    bgcolor: "rgba(255,255,255,0.2)",
                    overflow: "hidden",
                    mt: 0.5,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {validatedLogo ? (
                    <Box
                        width="100%"
                        height="100%"
                        sx={{
                            backgroundImage: `url(${validatedLogo})`,
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "cover",
                        }}
                    />
                ) : (
                    <ColoredIcon icon={DefaultIcon} size={48} color="white" />
                )}
            </Box>
        ) : undefined;

    return (
        <OnboardingShell
            variant="form"
            step={3}
            totalSteps={3}
            heroTitle={
                isClaimingPreviewBrand ? "Claim your app" : "Almost there!"
            }
            heroSubtitle={
                isClaimingPreviewBrand
                    ? `${claimBrandName} is waiting for you`
                    : "Create your account"
            }
            heroContent={heroContent}
            bottomButton={
                <BottomButton
                    onClick={() => standardSignUp()}
                    disabled={loading}
                    color="primary"
                    sticky
                >
                    Create Account
                </BottomButton>
            }
        >
            <Stack
                className="force-dynamic-translation"
                spacing={2.5}
                sx={{ width: "100%" }}
            >
                {/* Google Sign In — quick option */}
                <Box
                    role="button"
                    onClick={() => {
                        if (!loading) {
                            setLoading(true);
                            googleLogin();
                        }
                    }}
                    sx={{
                        ...CARD_STYLES,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        py: 1.75,
                        border: "1px solid rgba(0,0,0,0.08)",
                        transition:
                            "transform 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow:
                                "0px 4px 16px rgba(0, 0, 0, 0.12)",
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={22} />
                    ) : (
                        <>
                            <img
                                src={GoogleIcon}
                                alt="Google"
                                style={{ width: 22, height: 22 }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: FONTS.display,
                                    fontWeight: 600,
                                    fontSize: "15px",
                                }}
                            >
                                Continue with Google
                            </Typography>
                        </>
                    )}
                </Box>

                {/* Divider */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ px: 1 }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            height: "1px",
                            bgcolor: "divider",
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: FONTS.body,
                            fontSize: "12px",
                            color: "text.disabled",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        or sign up with email
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            height: "1px",
                            bgcolor: "divider",
                        }}
                    />
                </Stack>

                {/* Email form fields — always visible */}
                <Stack spacing={1.5} width="100%">
                    <Stack spacing={0.5}>
                        <Typography sx={FORM_LABEL_SX}>Full name</Typography>
                        <TextField
                            variant="contained"
                            fullWidth
                            placeholder="Your name"
                            value={name}
                            onChange={(e) =>
                                setName(e.currentTarget.value)
                            }
                            nextRef={emailInputRef}
                        />
                    </Stack>
                    <Stack spacing={0.5}>
                        <Typography sx={FORM_LABEL_SX}>Email</Typography>
                        <TextField
                            variant="contained"
                            fullWidth
                            placeholder="email@gmail.com"
                            value={email}
                            onChange={(e) =>
                                setEmail(
                                    e.currentTarget.value.toLocaleLowerCase()
                                )
                            }
                            ref={emailInputRef}
                            nextRef={passwordInputRef}
                        />
                    </Stack>
                    <Stack spacing={0.5}>
                        <Typography sx={FORM_LABEL_SX}>Password</Typography>
                        <PasswordField
                            variant="contained"
                            fullWidth
                            placeholder="Password"
                            value={password}
                            onChange={(e) =>
                                setPassword(e.currentTarget.value)
                            }
                            ref={passwordInputRef}
                            onEnter={standardSignUp}
                        />
                    </Stack>
                </Stack>
            </Stack>
        </OnboardingShell>
    );
};

export default SignUp;
